import { memo } from "react";
import { Calendar, Clock, Users, Lock, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { ProviderType, getBookingConfig } from "@/types/booking";
import { getBookingTheme } from "@/config/bookingThemes";
import { useUniversalServices } from "@/hooks/useUniversalServices";
import { useUniversalBookingAccess } from "@/hooks/useUniversalBookingAccess";
import { useUniversalAvailability } from "@/hooks/useUniversalAvailability";
import { UniversalBookingWidget } from "./UniversalBookingWidget";

interface UniversalPublicBookingSectionProps {
  providerType: ProviderType;
  providerId: string;
  providerName: string;
  providerSlug?: string;
  className?: string;
}

// Premium service preview card
const ServicePreview = memo(({ 
  name, 
  duration, 
  price, 
  maxParticipants,
  theme,
  index,
}: { 
  name: string;
  duration: number;
  price: number;
  maxParticipants: number;
  theme: ReturnType<typeof getBookingTheme>;
  index: number;
}) => {
  const ThemeIcon = theme.icon;
  
  return (
    <div 
      className={cn(
        "group flex items-center justify-between p-3 sm:p-4 bg-background rounded-xl border-2 transition-all duration-300",
        "hover:border-primary/50 hover:shadow-lg hover:-translate-y-0.5",
        "touch-manipulation animate-slide-up-fade"
      )}
      style={{ animationDelay: `${index * 100}ms` }}
    >
      <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
        {/* Icon container */}
        <div className={cn(
          "w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0",
          "transition-all duration-300 group-hover:scale-110",
          `bg-gradient-to-br ${theme.gradientLight}`
        )}>
          <ThemeIcon className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
        </div>
        
        <div className="min-w-0">
          <div className="font-semibold text-sm sm:text-base truncate">{name}</div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
            <Badge variant="secondary" className="rounded-full text-xs">
              <Clock className="h-3 w-3 mr-1" />
              {duration} min
            </Badge>
            {maxParticipants > 1 && (
              <Badge variant="secondary" className="rounded-full text-xs">
                <Users className="h-3 w-3 mr-1" />
                Max {maxParticipants}
              </Badge>
            )}
          </div>
        </div>
      </div>
      
      {/* Price with gradient */}
      <div className={cn(
        "text-xl sm:text-2xl font-bold flex-shrink-0 ml-3",
        `bg-gradient-to-r ${theme.gradient} bg-clip-text text-transparent`
      )}>
        {price}€
      </div>
    </div>
  );
});
ServicePreview.displayName = "ServicePreview";

// Placeholder service for inactive booking
const PlaceholderService = memo(({ 
  index, 
  theme 
}: { 
  index: number;
  theme: ReturnType<typeof getBookingTheme>;
}) => {
  const placeholders = [
    { name: "Sesión Individual", duration: 60, price: 45 },
    { name: "Pack 5 Sesiones", duration: 60, price: 200 },
    { name: "Consulta Online", duration: 45, price: 35 },
  ];
  const service = placeholders[index % placeholders.length];
  const ThemeIcon = theme.icon;
  
  return (
    <div className="flex items-center justify-between p-3 sm:p-4 bg-muted/30 rounded-xl border-2 border-dashed border-muted opacity-60">
      <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
        <div className={cn(
          "w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0",
          "bg-muted/50"
        )}>
          <ThemeIcon className="h-5 w-5 sm:h-6 sm:w-6 text-muted-foreground/50" />
        </div>
        <div className="min-w-0">
          <div className="font-medium text-sm sm:text-base truncate text-muted-foreground">{service.name}</div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground/70 mt-0.5">
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {service.duration} min
            </span>
          </div>
        </div>
      </div>
      <div className="text-xl sm:text-2xl font-bold text-muted-foreground/40 flex-shrink-0 ml-3">{service.price}€</div>
    </div>
  );
});
PlaceholderService.displayName = "PlaceholderService";

export const UniversalPublicBookingSection = memo(function UniversalPublicBookingSection({
  providerType,
  providerId,
  providerName,
  providerSlug,
  className,
}: UniversalPublicBookingSectionProps) {
  const config = getBookingConfig(providerType);
  const theme = getBookingTheme(providerType);
  const ThemeIcon = theme.icon;
  
  const { activeServices, isLoading: servicesLoading } = useUniversalServices(providerType, providerId);
  const { isLoading: availabilityLoading } = useUniversalAvailability(providerType, providerId);
  const access = useUniversalBookingAccess(providerType, providerId);

  const isLoading = servicesLoading || availabilityLoading || access.isLoading;
  const isBookingEnabled = access.hasAccess;
  const hasServicesConfigured = activeServices.length > 0;
  const canBookNow = isBookingEnabled && hasServicesConfigured;

  if (isLoading) {
    return (
      <Card className={cn("overflow-hidden", className)}>
        <div className={cn("h-2 w-full", `bg-gradient-to-r ${theme.gradient} opacity-50`)} />
        <CardContent className="p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 rounded-2xl bg-muted animate-pulse" />
            <div className="flex-1 space-y-2">
              <div className="h-5 bg-muted rounded animate-pulse w-48" />
              <div className="h-4 bg-muted rounded animate-pulse w-32" />
            </div>
          </div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-20 bg-muted rounded-xl animate-pulse" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn(
      "relative overflow-hidden border-2 shadow-xl",
      canBookNow ? "border-primary/30" : "border-muted",
      className
    )}>
      {/* Decorative gradient background */}
      <div 
        className="absolute inset-0 opacity-[0.03]"
        style={{ background: theme.bgPattern }}
      />
      
      {/* Top gradient line */}
      <div className={cn(
        "h-1.5 w-full",
        `bg-gradient-to-r ${theme.gradient}`,
        !canBookNow && "opacity-50"
      )} />
      
      <CardHeader className={cn(
        "relative border-b p-4 sm:p-6",
        `bg-gradient-to-r ${theme.gradientLight}`
      )}>
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          {/* Animated icon container */}
          <div className={cn(
            "w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center flex-shrink-0",
            "shadow-lg transition-transform hover:scale-105",
            canBookNow 
              ? `bg-gradient-to-br ${theme.gradient}`
              : "bg-muted"
          )}>
            {canBookNow ? (
              <Calendar className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
            ) : (
              <Lock className="h-7 w-7 sm:h-8 sm:w-8 text-muted-foreground" />
            )}
          </div>
          
          <div className="flex-1 min-w-0">
            <CardTitle className="text-xl sm:text-2xl flex items-center gap-2 flex-wrap">
              <span>{theme.emoji}</span>
              <span>Reservar {config.serviceLabels.singular}</span>
            </CardTitle>
            <CardDescription className="text-sm sm:text-base mt-1">
              {canBookNow
                ? theme.motivationalText
                : isBookingEnabled
                  ? "Reservas activadas, falta configurar servicios"
                  : "Sistema de reservas próximamente disponible"}
            </CardDescription>
          </div>
          
          {canBookNow ? (
            <UniversalBookingWidget
              providerType={providerType}
              providerId={providerId}
              providerName={providerName}
              providerSlug={providerSlug}
              className="w-full sm:w-auto"
            />
          ) : (
            <Button 
              variant="outline" 
              disabled 
              className="w-full sm:w-auto opacity-60"
            >
              <Lock className="w-4 h-4 mr-2" />
              {isBookingEnabled ? "Configura servicios" : "Próximamente"}
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="relative p-4 sm:p-6">
        {canBookNow ? (
          /* Active booking - show real services */
          <div>
            <h4 className="font-semibold text-sm sm:text-base mb-4 flex items-center gap-2">
              <ThemeIcon className="h-4 w-4 text-primary" />
              {config.serviceLabels.plural} Disponibles
            </h4>
            <div className="space-y-3">
              {activeServices.slice(0, 3).map((service, index) => (
                <ServicePreview
                  key={service.id}
                  name={service.name}
                  duration={service.duration_minutes}
                  price={service.price}
                  maxParticipants={service.max_participants}
                  theme={theme}
                  index={index}
                />
              ))}
            </div>
            {activeServices.length > 3 && (
              <p className="text-xs text-muted-foreground text-center mt-4">
                +{activeServices.length - 3} {config.serviceLabels.plural.toLowerCase()} más
              </p>
            )}
          </div>
        ) : (
          /* Not bookable yet - show placeholder preview */
          <div className="relative">
            <h4 className="font-medium text-sm mb-4 text-muted-foreground flex items-center gap-2">
              <ThemeIcon className="h-4 w-4" />
              {config.serviceLabels.plural} (Vista Previa)
            </h4>
            <div className="space-y-3">
              {[0, 1, 2].map((index) => (
                <PlaceholderService key={index} index={index} theme={theme} />
              ))}
            </div>
            
            {/* Overlay message */}
            <div className={cn(
              "mt-6 p-4 sm:p-5 rounded-xl border-2 border-dashed text-center",
              `bg-gradient-to-r ${theme.gradientLight} border-primary/20`
            )}>
              <div className={cn(
                "w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-3",
                `bg-gradient-to-br ${theme.gradient} opacity-80`
              )}>
                <Sparkles className="h-6 w-6 text-white" />
              </div>
              <p className="text-sm font-medium text-foreground">
                {isBookingEnabled
                  ? "Reservas activadas, pero aún no hay servicios configurados."
                  : "El sistema de reservas aún no está activado."}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {isBookingEnabled
                  ? "Añade tus servicios y disponibilidad para que el calendario se muestre a tus clientes."
                  : "Pronto podrás reservar citas directamente."}
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
});
